import { inject } from '@angular/core';
import { HttpErrorResponse } from '@angular/common/http';
import { CanActivateFn, Router, UrlTree } from '@angular/router';
import { catchError, map, of } from 'rxjs';
import { AuthService } from '../services/auth.service';
import { PocketbaseService } from '../services/pocketbase.service';

// Recognized failure bodies from POST /api/auth/sso (see AuthService.ssoLogin on the backend).
type SsoFailureReason = 'account_pending' | 'account_rejected';

function ssoBlockedUrlTree(router: Router, reason?: SsoFailureReason): UrlTree {
  return router.createUrlTree(['/sso-required'], reason ? { queryParams: { reason } } : undefined);
}

/**
 * Guards every route that requires a Fraternity Wars session.
 *
 * Resolution order:
 *  1. Already have a Fraternity Wars access token in memory -> allow.
 *  2. Try to refresh the FW session from the `fw_refresh` httpOnly cookie -> allow on success.
 *  3. No FW session at all: check whether the central PocketBase account
 *     (pb.scuvanya.online, mirrored via a `.scuvanya.online` cookie - see PocketbaseService) is
 *     logged in. If so, silently bridge it into a FW session via POST /api/auth/sso and allow.
 *  4. Neither exists (or the PocketBase account is pending/rejected): block, no login form here
 *     - access is exclusively via the central account. Redirect to /sso-required with a plain
 *     explanation instead.
 *
 * This does NOT touch the existing /login and /register flow - those keep working unchanged
 * for the local email/password login model; this guard only adds the SSO bridge on top.
 */
export const authGuard: CanActivateFn = () => {
  const auth = inject(AuthService);
  const pocketbase = inject(PocketbaseService);
  const router = inject(Router);

  if (auth.isAuthenticated()) {
    return true;
  }

  return auth.refresh().pipe(
    map(() => true),
    catchError(() => {
      if (!pocketbase.pb.authStore.isValid) {
        return of(ssoBlockedUrlTree(router));
      }

      return auth.ssoLogin(pocketbase.pb.authStore.token).pipe(
        map(() => true),
        catchError((ssoError: unknown) => {
          const reason =
            ssoError instanceof HttpErrorResponse && ssoError.status === 403
              ? (ssoError.error?.error as SsoFailureReason | undefined)
              : undefined;
          return of(ssoBlockedUrlTree(router, reason));
        }),
      );
    }),
  );
};
